import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { get } from "lodash";
import { useForm, useFormState } from "react-final-form";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import ScoreIcon from "rs-components/ScoreIcon";
import OutputContent from "./components/OutputContent";
import SimpleRemoveButton from "components/SimpleRemoveButton";
import RemoveConfirmDialog from "rs-components/RemoveConfirmDialog";
import IconButton from "components/IconButton";
import { processVariablesSelector } from "store/global/global.selectors";
import { InsightOutputTypes, } from "store/rs-workspace/rs-workspace.types";
import { checkOutputUsed, getInsightPreviewOperationPending, getInsightLatestScoreData, checkCanDisplayScore, prepareBaseScoreItem, } from "store/rs-workspace/utils/insights.utils";
import { insightExpandedInputSetIndexSelector, persistedInsightsDataSelector, previewRequestsSelector, workspaceCurrentInsightIdSelector, workspaceOptimizationsSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getResultSetsSelector } from "store/rs-workspace/selectors/results.selectors";
import { deleteTargetSelector, isEditableSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { RemovableEntityType } from "store/knowledge-base-item/knowledge-base-item.types";
import { setDeleteTarget } from "store/knowledge-base-item/knowledge-base-item.actions";
import { removeInsightOutputScores } from "store/rs-workspace/actions/rs-workspace.actions";
import { getDefaultOutput } from "store/rs-workspace/utils/insights.utils";
import { InsightFormFields } from "../..";
import { InsightLabels } from "shared/labels";
import { isNotEmpty } from "utils/validate";
import ComparisonIcon from "icons/comparison.svg";
import EvaluationIcon from "icons/evaluation.svg";
import InfoIcon from "icons/info-square-black.svg";
import DeleteIcon from "icons/delete.svg";
import * as styles from "./styles.module.scss";
const Output = ({ name, index, restrictions }) => {
    const formState = useFormState();
    const form = useForm();
    const fieldValue = get(formState.values, name);
    const dispatch = useDispatch();
    const editable = useSelector(isEditableSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const previewRequests = useSelector(previewRequestsSelector);
    const currentInsightId = useSelector(workspaceCurrentInsightIdSelector);
    const processVariables = useSelector(processVariablesSelector);
    const expandedInputSetIndex = useSelector(insightExpandedInputSetIndexSelector);
    const deleteTarget = useSelector(deleteTargetSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const resultSets = useSelector(getResultSetsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const isComparisonNode = useMemo(() => {
        const type = get(formState.values, `${name}.${InsightFormFields.type}`);
        return type === InsightOutputTypes.Comparison;
    }, [formState.values, name]);
    const currentOutputRemovableType = useMemo(() => isComparisonNode
        ? RemovableEntityType.INSIGHT_COMPARISON_OUTPUT
        : RemovableEntityType.INSIGHT_EVALUATION_OUTPUT, [isComparisonNode]);
    const outputIsUsed = useMemo(() => checkOutputUsed(fieldValue.id, currentInsightId, optimizations, resultSets), [optimizations, fieldValue.id, currentInsightId, resultSets]);
    const outputNotPristine = useMemo(() => {
        const { sourceA, sourceB, processVariable, operation, transformationOperation, advancedEnabled } = fieldValue;
        return ([sourceA, sourceB, processVariable, operation, transformationOperation].some((item) => isNotEmpty(item)) || advancedEnabled);
    }, [fieldValue]);
    const scores = useMemo(() => {
        const insightScoreData = getInsightLatestScoreData(persistedInsightsData, currentInsightId);
        const outputScores = insightScoreData?.scores.filter((score) => score.outputId === fieldValue.id);
        const inputSets = get(formState.values, InsightFormFields.inputSets);
        return inputSets.map((inputSet, inputSetIndex) => {
            const scoreEntity = outputScores?.find((score) => score.inputSetId === inputSet.id);
            const scorePending = getInsightPreviewOperationPending(previewRequests, currentInsightId, inputSet.id, fieldValue.id);
            return {
                ...prepareBaseScoreItem(scoreEntity?.score, checkCanDisplayScore(fieldValue, inputSet.inputs, processVariables, recipes)),
                pending: scorePending,
                highlighted: inputSetIndex === expandedInputSetIndex,
            };
        });
    }, [
        persistedInsightsData,
        currentInsightId,
        formState.values,
        fieldValue,
        previewRequests,
        processVariables,
        recipes,
        expandedInputSetIndex,
    ]);
    const isEmpty = useMemo(() => fieldValue.type === null, [fieldValue.type]);
    const onAddEvaluation = useCallback(() => {
        form.mutators.update(InsightFormFields.outputs, index, {
            ...fieldValue,
            [InsightFormFields.type]: InsightOutputTypes.Evaluation,
        });
    }, [fieldValue, form.mutators, index]);
    const onAddComparison = useCallback(() => {
        form.mutators.update(InsightFormFields.outputs, index, {
            ...fieldValue,
            [InsightFormFields.type]: InsightOutputTypes.Comparison,
        });
    }, [fieldValue, form.mutators, index]);
    const deleteOutput = useCallback((isConfirmed) => {
        if (!isConfirmed) {
            return;
        }
        const formOutputs = get(formState.values, [InsightFormFields.outputs]);
        const untouchedOutputs = formOutputs?.filter((item) => item.id !== fieldValue.id);
        const updatedOutputs = [
            ...untouchedOutputs,
            { ...getDefaultOutput(restrictions), id: fieldValue.id },
        ].sort((a, b) => a.id - b.id);
        form.change(InsightFormFields.outputs, updatedOutputs);
        dispatch(removeInsightOutputScores({ insightId: currentInsightId ?? -1, outputId: fieldValue.id }));
        dispatch(setDeleteTarget(null));
    }, [formState.values, restrictions, fieldValue.id, form, dispatch, currentInsightId]);
    const setOutputDeleteTarget = useCallback(() => {
        dispatch(setDeleteTarget({
            id: fieldValue.id,
            type: currentOutputRemovableType,
            warning: InsightLabels.OUTPUT_USED_HINT,
        }));
    }, [dispatch, fieldValue.id, currentOutputRemovableType]);
    const renderEmptyState = useCallback(() => {
        return (React.createElement("div", { className: styles.emptyState },
            React.createElement("div", { className: styles.content },
                React.createElement("div", { className: styles.info },
                    React.createElement(InfoIcon, { className: styles.icon })),
                React.createElement("h5", null, "Configure Assessment"),
                React.createElement("p", { className: styles.text },
                    "Add an Evaluation to assess how well an important aspect of your process matches up to your expectations in a specific input. ",
                    React.createElement("br", null),
                    React.createElement("br", null),
                    " Add a Comparison to assess how well two inputs correspond in terms of some important aspect."),
                editable && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { onClick: onAddEvaluation, classes: { root: styles.add }, label: "Add evaluation", role: "button", style: ButtonStyle.Tertiary, size: ButtonSize.Normal }),
                    React.createElement(Button, { onClick: onAddComparison, classes: { root: styles.add }, label: "Add comparison", role: "button", style: ButtonStyle.Tertiary, size: ButtonSize.Normal }))))));
    }, [editable, onAddEvaluation, onAddComparison]);
    const renderRemoveButton = useCallback(() => {
        if (!outputIsUsed) {
            return outputNotPristine ? (React.createElement(SimpleRemoveButton, { className: classNames(styles.delete, styles.padded), onOpen: setOutputDeleteTarget, action: deleteOutput, isMenuItem: false })) : (React.createElement(DeleteIcon, { className: styles.delete, onClick: () => deleteOutput(true) }));
        }
        return (React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), classes: { root: styles.delete }, title: "Delete", onClick: setOutputDeleteTarget }));
    }, [outputIsUsed, outputNotPristine, deleteOutput, setOutputDeleteTarget]);
    const renderRemoveDialog = useCallback(() => {
        if (deleteTarget === null ||
            deleteTarget.type !== currentOutputRemovableType ||
            deleteTarget.id !== fieldValue.id) {
            return null;
        }
        if (outputIsUsed) {
            return (React.createElement(RemoveConfirmDialog, { title: `Delete ${deleteTarget.type}`, action: () => deleteOutput(true), message: deleteTarget.warning ?? "" }));
        }
    }, [deleteTarget, deleteOutput, outputIsUsed, fieldValue.id, currentOutputRemovableType]);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: classNames(styles.header, { [styles.empty]: isEmpty }) },
            React.createElement("div", { className: styles.left },
                fieldValue.type === InsightOutputTypes.Evaluation ? React.createElement(EvaluationIcon, null) : React.createElement(ComparisonIcon, null),
                React.createElement("span", { className: styles.title },
                    index + 1,
                    ". ",
                    isEmpty ? "Empty" : fieldValue.type)),
            !isEmpty && (React.createElement("div", { className: styles.right },
                editable && renderRemoveButton(),
                scores.map((scoreItem, index) => (React.createElement(ScoreIcon, { key: `score-${index}`, className: styles.score, ...scoreItem })))))),
        isEmpty && renderEmptyState(),
        !isEmpty && (React.createElement("div", { className: styles.body },
            React.createElement(OutputContent, { name: name, index: index, restrictions: restrictions }))),
        renderRemoveDialog()));
};
export default Output;
